import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { PostResponseSchema } from '@/api/client';
import { PostContent, type MediaInfo } from './PostContent';
import { ImageModal } from './ImageModal';
import { VideoPlayerModal } from '@/feature/play-video/VideoPlayerModal';
import { useAdminContext } from '../contexts/AdminProvider';

interface Props {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  pinnedPosts: PostResponseSchema[];
  unpinnedPosts: PostResponseSchema[];
  isSearching: boolean;
  isFetching: boolean;
  onPinPost: (postId: number, pinOrder?: number) => Promise<void>;
  onUnpinPost: (postId: number) => Promise<void>;
  onReorderPinnedPosts: (posts: PostResponseSchema[]) => Promise<void>;
  isPinning: boolean;
  isUnpinning: boolean;
  isReordering: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
}

export function PinnedPostsView({
  searchQuery,
  onSearchChange,
  pinnedPosts,
  unpinnedPosts,
  isSearching,
  isFetching,
  onPinPost,
  onUnpinPost,
  onReorderPinnedPosts,
  isPinning,
  isUnpinning,
  isReordering,
  hasMore,
  onLoadMore,
}: Props) {
  const { showConfirmation } = useAdminContext();
  const [activeMedia, setActiveMedia] = useState<MediaInfo | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const handleMediaClick = useCallback((media: MediaInfo) => {
    setActiveMedia(media);
  }, []);

  const closeMedia = useCallback(() => {
    setActiveMedia(null);
  }, []);

  // Infinite scroll with IntersectionObserver
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetching) {
          onLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, isFetching, onLoadMore]);

  // Use props directly - pinned posts come from dedicated query, already sorted
  const pinnedResults = pinnedPosts;
  const unpinnedResults = unpinnedPosts;

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = pinnedResults.findIndex((p) => p.id === active.id);
        const newIndex = pinnedResults.findIndex((p) => p.id === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
          const reordered = arrayMove(pinnedResults, oldIndex, newIndex);
          onReorderPinnedPosts(reordered);
        }
      }
    },
    [pinnedResults, onReorderPinnedPosts]
  );

  const handlePinClick = useCallback(
    (post: PostResponseSchema) => {
      if (post.pin_order != null) {
        showConfirmation(
          `Unpin "${post.title || post.message_text?.slice(0, 30) || 'this post'}"?`,
          () => onUnpinPost(post.id)
        );
      } else {
        onPinPost(post.id);
      }
    },
    [onPinPost, onUnpinPost, showConfirmation]
  );

  return (
    <div className="pinned-posts-view">
      <div className="pinned-search-section">
        <input
          type="text"
          className="admin-search-input pinned-search-input"
          placeholder="Search posts by title or message..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        {isSearching && <span className="pinned-search-spinner" />}
      </div>

      {pinnedResults.length === 0 && unpinnedResults.length === 0 && !isSearching ? (
        <div className="pinned-empty-state">
          <h3 className="pinned-empty-title">No posts found</h3>
          <p className="pinned-empty-description">
            {searchQuery
              ? `No posts match "${searchQuery}". Try a different search term.`
              : 'No posts available.'}
          </p>
        </div>
      ) : (
        <>
          {pinnedResults.length > 0 && (
            <section className="pinned-section">
              <h2 className="pinned-section-title">
                Pinned ({pinnedResults.length})
                {isReordering && <span className="pinned-reordering-badge">Saving...</span>}
              </h2>
              <p className="pinned-section-hint">Drag to reorder</p>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={pinnedResults.map((p) => p.id)}
                  strategy={horizontalListSortingStrategy}
                >
                  <div className="pinned-posts-grid pinned-posts-sortable">
                    {pinnedResults.map((post) => (
                      <SortablePostCard
                        key={post.id}
                        post={post}
                        onPinToggle={() => handlePinClick(post)}
                        onMediaClick={handleMediaClick}
                        isLoading={isUnpinning || isReordering}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </section>
          )}

          {unpinnedResults.length > 0 && (
            <section className="pinned-section">
              <h2 className="pinned-section-title">
                {searchQuery ? `Search Results (${unpinnedResults.length})` : `All Posts (${unpinnedResults.length})`}
              </h2>
              <div className="pinned-posts-grid">
                {unpinnedResults.map((post) => (
                  <PostCard
                    key={post.id}
                    post={post}
                    onPinToggle={() => handlePinClick(post)}
                    onMediaClick={handleMediaClick}
                    isLoading={isPinning}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Infinite scroll trigger */}
          {hasMore && (
            <div ref={loadMoreRef} className="pinned-load-more">
              {isFetching && <span className="pinned-search-spinner" />}
            </div>
          )}
        </>
      )}

      {activeMedia?.type === 'image' && (
        <ImageModal src={activeMedia.src} alt="Post content" onClose={closeMedia} />
      )}

      {activeMedia?.type === 'video' && (
        <VideoPlayerModal
          isOpen={true}
          videoUrl={activeMedia.src}
          onClose={closeMedia}
          playing={true}
          controls={true}
        />
      )}

      {activeMedia?.type === 'audio' && (
        <AudioModal src={activeMedia.src} onClose={closeMedia} />
      )}
    </div>
  );
}

interface PostCardProps {
  post: PostResponseSchema;
  onPinToggle: () => void;
  onMediaClick: (media: MediaInfo) => void;
  isLoading: boolean;
}

function SortablePostCard({ post, onPinToggle, onMediaClick, isLoading }: PostCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: post.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`pinned-post-card is-pinned ${isDragging ? 'is-dragging' : ''}`}
    >
      <div className="pinned-post-drag-handle" {...attributes} {...listeners}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <path d="M7 2a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM7 5a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM7 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm-3 3a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm-3 3a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/>
        </svg>
      </div>

      <div className="pinned-post-badge">
        <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
          <path d="M4.146.146A.5.5 0 0 1 4.5 0h7a.5.5 0 0 1 .5.5c0 .68-.342 1.174-.646 1.479-.126.125-.25.224-.354.298v4.431l.078.048c.203.127.476.314.751.555C12.36 7.775 13 8.527 13 9.5a.5.5 0 0 1-.5.5h-4v4.5c0 .276-.224 1.5-.5 1.5s-.5-1.224-.5-1.5V10h-4a.5.5 0 0 1-.5-.5c0-.973.64-1.725 1.17-2.189A5.921 5.921 0 0 1 5 6.708V2.277a2.77 2.77 0 0 1-.354-.298C4.342 1.674 4 1.179 4 .5a.5.5 0 0 1 .146-.354z"/>
        </svg>
        #{post.pin_order}
      </div>

      <div className="pinned-post-media">
        <PostContent post={post} onMediaClick={onMediaClick} />
      </div>

      <div className="pinned-post-content">
        <div className="pinned-post-header">
          <span className="pinned-post-type">{post.post_type}</span>
          <span className="pinned-post-id">#{post.id}</span>
        </div>

        {post.title && <h3 className="pinned-post-title">{post.title}</h3>}

        {post.message_text && (
          <p className="pinned-post-message">
            {post.message_text.length > 120
              ? `${post.message_text.slice(0, 120)}...`
              : post.message_text}
          </p>
        )}

        <div className="pinned-post-meta">
          <span>@{post.username}</span>
          <span>{new Date(post.timestamp).toLocaleDateString()}</span>
        </div>
      </div>

      <div className="pinned-post-actions">
        <button
          className="admin-btn admin-btn-danger"
          onClick={onPinToggle}
          disabled={isLoading}
        >
          Unpin
        </button>
      </div>
    </div>
  );
}

function PostCard({ post, onPinToggle, onMediaClick, isLoading }: PostCardProps) {
  const isPinned = post.pin_order != null;

  return (
    <div className={`pinned-post-card ${isPinned ? 'is-pinned' : ''}`}>
      {isPinned && (
        <div className="pinned-post-badge">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
            <path d="M4.146.146A.5.5 0 0 1 4.5 0h7a.5.5 0 0 1 .5.5c0 .68-.342 1.174-.646 1.479-.126.125-.25.224-.354.298v4.431l.078.048c.203.127.476.314.751.555C12.36 7.775 13 8.527 13 9.5a.5.5 0 0 1-.5.5h-4v4.5c0 .276-.224 1.5-.5 1.5s-.5-1.224-.5-1.5V10h-4a.5.5 0 0 1-.5-.5c0-.973.64-1.725 1.17-2.189A5.921 5.921 0 0 1 5 6.708V2.277a2.77 2.77 0 0 1-.354-.298C4.342 1.674 4 1.179 4 .5a.5.5 0 0 1 .146-.354z"/>
          </svg>
          #{post.pin_order}
        </div>
      )}

      <div className="pinned-post-media">
        <PostContent post={post} onMediaClick={onMediaClick} />
      </div>

      <div className="pinned-post-content">
        <div className="pinned-post-header">
          <span className="pinned-post-type">{post.post_type}</span>
          <span className="pinned-post-id">#{post.id}</span>
        </div>

        {post.title && <h3 className="pinned-post-title">{post.title}</h3>}

        {post.message_text && (
          <p className="pinned-post-message">
            {post.message_text.length > 120
              ? `${post.message_text.slice(0, 120)}...`
              : post.message_text}
          </p>
        )}

        <div className="pinned-post-meta">
          <span>@{post.username}</span>
          <span>{new Date(post.timestamp).toLocaleDateString()}</span>
        </div>
      </div>

      <div className="pinned-post-actions">
        <button
          className={`admin-btn ${isPinned ? 'admin-btn-danger' : 'admin-btn-primary'}`}
          onClick={onPinToggle}
          disabled={isLoading}
        >
          {isPinned ? 'Unpin' : 'Pin to Feed'}
        </button>
      </div>
    </div>
  );
}

function AudioModal({ src, onClose }: { src: string; onClose: () => void }) {
  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div
        className="admin-modal"
        style={{ maxWidth: '360px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admin-modal-header">
          <h3 className="admin-modal-title">Audio Player</h3>
        </div>
        <div className="admin-modal-body" style={{ padding: '24px' }}>
          <audio src={src} controls autoPlay style={{ width: '100%' }}>
            Your browser does not support the audio element.
          </audio>
        </div>
        <div className="admin-modal-footer">
          <button className="admin-btn admin-btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
